import React, { createRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ButtonLink } from 'styled/components/Button';
import ActionLink from 'styled/components/ActionLink';

import StatusButton from 'components/StatusButtons';
import StatusDropdown from 'components/StatusDropdown';
import MatchWatch from 'components/MatchWatch/index';
import MatchPeriod from 'components/MatchPeriod';
import EmptyCacheButton from 'components/EmptyCacheButton';

import useOnClickOutside from 'hooks/useOnClickOutside';

import {
  selectMatch,
  matchSliceActions,
  selectShowStatusDropdown,
  selectMatchStatusId,
  selectMatchEvents,
} from 'features/match/matchSlice';

import { modalsActions } from 'features/modals/modalsSlice';

import ResultInput from './ResultInput';

import StyledHeader from './StyledHeader';

import IconButton from 'atoms/IconButton';
import helpIcon from 'assets/images/icons/help.svg';
import Score from './Score';
import ConnectionWarning from './ConnectionWarning';
import hasPenaltiesShootoutStarted from '../../helpers/hasPenaltiesShootoutStarted';

const Header: React.FC = () => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const match = useSelector(selectMatch);
  const matchEvents = useSelector(selectMatchEvents);
  const matchStatusId = useSelector(selectMatchStatusId);
  const showStatusDropdown = useSelector(selectShowStatusDropdown);
  const { REACT_APP_ADMIN_URL } = process.env;

  const penaltiesShootoutStarted = hasPenaltiesShootoutStarted(matchEvents);

  const { round, stage } = match;

  const liveFeed = match.liveFeeds ? match.liveFeeds[0] : null;
  const liveFeedId = liveFeed ? liveFeed.id : null;

  const settingsURL = `${REACT_APP_ADMIN_URL}/setupLiveFeed.php?liveFeedId=${liveFeedId}`;

  const ref = createRef<HTMLDivElement>();

  const helpAlt = intl.formatMessage({ id: 'header.help' });

  const onClickOutside = () => {
    if (showStatusDropdown) {
      dispatch(matchSliceActions.setStatusDropdown(false));
    }
  };

  useOnClickOutside(ref, onClickOutside);

  return (
    <StyledHeader>
      <div className="header-content">
        <div className="left">
          <div>
            <ButtonLink href={settingsURL} target="_parent">
              <FormattedMessage id="buttons.settings" description="settings button" />
            </ButtonLink>
            <br />
            <br />
            <EmptyCacheButton />
          </div>
          <div className="help">
            <IconButton
              icon={helpIcon}
              alt={helpAlt}
              noText={true}
              width={30}
              onClick={() => {
                dispatch(modalsActions.setInfoModal(true));
              }}
            />
          </div>
        </div>
        <div className="middle">
          <ConnectionWarning />
          <Score />
          <div className="round-container">
            <div className="round">
              <FormattedMessage
                id="header.stageAndRound"
                values={{
                  stage: stage.name,
                  round,
                }}
              />
              <MatchPeriod />
            </div>
            <ResultInput />
          </div>
        </div>
        <div className="right">
          <div>
            <div className="time">
              <MatchWatch />
            </div>
          </div>
          <div className="change-status">
            <StatusButton matchStatusId={matchStatusId} penaltiesShootoutStarted={penaltiesShootoutStarted} />
            <div className="dropdown-container">
              <div className="inner" ref={ref}>
                <ActionLink
                  onClick={() => {
                    dispatch(matchSliceActions.toggleStatusDropdown());
                  }}
                >
                  <FormattedMessage id="header.changeStatus" />
                </ActionLink>
                <StatusDropdown />
              </div>
            </div>
          </div>
        </div>
      </div>
    </StyledHeader>
  );
};

export default Header;
